import React, { ReactElement, useState } from "react";
import styles from "./TransactionDetailsPage.module.scss";
import { Accordion, AccordionItem, Box, Group } from "@mantine/core";
import { Link } from "react-router-dom";
import { CChainInputOutput, ExportImportType, TransactionInOut } from "../../models/Transaction";
import { IconSquareRoundedChevronDownFilled } from "@tabler/icons-react";
import { LabelValueStack } from "../../components/labelValueStack/LabelValueStack.component";
import { convertFromWei, formatAddress, formatNumber } from "../../utils/formatter";
import { InputOutputTransactionType } from "../../models/enums";
import TransactionNavigatorButton from "./TransactionNavigatorButton";

type TransactionInputOutputProps = {
  inputs?: TransactionInOut[];
  outputs?: TransactionInOut[];
  cChainInputs?: CChainInputOutput[];
  cChainOutputs?: CChainInputOutput[];
  nextId?: string | null;
  cChainTxType?: ExportImportType
};

export default function TransactionInputOutput(
  {
    inputs,
    outputs,
    cChainOutputs,
    cChainInputs,
    nextId,
    cChainTxType,
  }: TransactionInputOutputProps): ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const addressFormatterLength = 30;

  const getPrevNextUrl = (tnx: TransactionInOut, nextId?: string | null): string => {
    if (tnx.type === InputOutputTransactionType.EXPORT && nextId) {
      return `/tx/c-chain/${nextId}`;
    }
    if (tnx.outTxID) {
      return `/tx/${tnx.type === InputOutputTransactionType.IMPORT ? "c-chain/" : ""}${tnx.outTxID}`;
    } else if (tnx.nextTx) {
      return `/tx/${tnx.nextTx}`;
    } else {
      return "";
    }
  };

  const printInputOutput = (
    value: TransactionInOut[],
    type: "input" | "output",
  ) => {
    if (value.length > 0) {
      return (
        <div className={styles.boxContainer}>
          {value.map((el: TransactionInOut, key: number) => (
            <div className={styles.smallCard} key={key}>
              <div className={`${styles.cardContent} ${styles[type]}`}>
                <Group
                  wrap={"nowrap"}
                  w={"100%"}
                  justify="space-between"
                  gap={0}
                >
                  <Box display={"flex"}>
                    <Box display={"flex"}>
                      <span className={styles.label}>Index:&nbsp;</span>
                    </Box>
                    <span>{el.index} </span>
                  </Box>
                </Group>

                <Group wrap={"nowrap"} gap={0}>
                  <span className={styles.label}>Amount:&nbsp;</span>
                  <span>{formatNumber(el.amount)}</span>
                  <span className={styles.label}>&nbsp;FLR </span>
                </Group>

                {el.address && (
                  <Group wrap={"nowrap"} gap={0} align={"baseline"}>
                    <span className={styles.label}>P-Address:&nbsp;</span>
                    <Link
                      className={styles.link}
                      to={(`/address/${el.address}`)}
                    >
                      {formatAddress(el.address, addressFormatterLength)}
                    </Link>
                  </Group>
                )}
                <Group wrap={"nowrap"} gap={0}>
                  <span className={styles.label}>Type:&nbsp;</span>
                  <span>{el.type === "TX" ? "UTXO" : el.type}</span>
                </Group>

                <Group
                  w="100%"
                  wrap={"nowrap"}
                  gap={0}
                  mt={5}
                  style={{
                    justifyContent: el.nextTx || (nextId && el.type === InputOutputTransactionType.EXPORT) ?
                      "flex-end" : "flex-start", // Dinamična poravnava
                  }}
                >
                  {((el.outTxID) ||
                      el.nextTx || (nextId && el.type === InputOutputTransactionType.EXPORT)) &&
                    <TransactionNavigatorButton link={getPrevNextUrl(el, nextId)}
                                                type={type === "input" ? "prev" : "next"} />}
                </Group>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return <></>;
  };

  const printCChainInputOutput = (
    value: CChainInputOutput[],
    type: "input" | "output",
  ) => {
    if (value.length > 0) {
      return (
        <div className={styles.boxContainer}>
          {value.map((el: CChainInputOutput, key: number) => (
            <div className={styles.smallCard} key={key}>
              <div className={`${styles.cardContent} ${styles[type]}`}>

                {el.value &&
                  <Group wrap={"nowrap"} gap={0}>
                    <span className={styles.label}>Amount:&nbsp;</span>
                    <span>{formatNumber(convertFromWei(el.value), false, 2, true)}</span>
                    <span className={styles.label}>&nbsp;FLR </span>
                  </Group>
                }

                {el.address?.address && (
                  <Group wrap={"nowrap"} gap={0} align={"baseline"}>
                    <span
                      className={styles.label}>C-Address:&nbsp;</span>
                    <Link
                      className={styles.link}
                      to={(`/address/${el.address?.address}`)}
                    >
                      {formatAddress(el.address.address, addressFormatterLength)}
                    </Link>
                  </Group>
                )}

                {el.address?.p_address && (
                  <Group wrap={"nowrap"} gap={0} align={"baseline"}>
                    <span
                      className={styles.label}>P-Address:&nbsp;</span>
                    <Link
                      className={styles.link}
                      to={(`/address/${el.address?.p_address}`)}
                    >
                      {formatAddress(el.address.p_address, addressFormatterLength)}
                    </Link>
                  </Group>
                )}

                {(el.addresses && el.addresses.length > 0) && (
                  <Group wrap={"nowrap"} gap={0} align={"baseline"}>
                    <span
                      className={styles.label}>P-Address:&nbsp;</span>
                    <Link
                      className={styles.link}
                      to={(`/address/${el.addresses![0]}`)}
                    >
                      {formatAddress(el.addresses.join(", "), addressFormatterLength)}
                    </Link>
                  </Group>
                )}

                <Group wrap={"nowrap"} gap={0}>
                  <span className={styles.label}>Type:&nbsp;</span>
                  {cChainTxType === ExportImportType.EXPORT ? (
                    <span>{type === "input" ? "UTXO" : "EXPORT"}</span>
                  ) : (
                    <span>{type === "input" ? "IMPORT" : "UTXO"}</span>
                  )}
                </Group>

                <Group
                  w="100%"
                  wrap={"nowrap"}
                  gap={0}
                  mt={5}
                  style={{
                    justifyContent: nextId && type === "output" ? "flex-end" : "flex-start", // Dinamična poravnava
                  }}
                >
                  {((el.tx_id && type === "input") || (nextId && type === "output")) &&
                    <TransactionNavigatorButton link={`/tx/${type === "input" ? el.tx_id : nextId}`}
                                                type={type === "input" ? "prev" : "next"} />}
                </Group>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return <></>;
  };

  return (
    <>
      <>
        <Accordion
          styles={{
            root: { width: "100%", minHeight: 50 },
            item: { border: "none" },
          }}
          classNames={{ chevron: styles.accordionChevron }}
          chevron={
            <IconSquareRoundedChevronDownFilled className={styles.icon} />
          }
        >
          <AccordionItem value="Inputs/outputs" style={{ minHeight: 50 }}>
            <Accordion.Control
              onClick={toggleAccordion}
              styles={{
                label: {
                  paddingLeft: "24px",
                  paddingRight: "0.5rem",
                  fontSize: "0.8rem",
                  color: "var( --quinary)",
                  margin: "6px 0",
                },
                control: { paddingLeft: "0px", height: 50 },
              }}
            >
              <Group wrap={"nowrap"} fw={"500"} className={styles.accordionTitle}>
                <LabelValueStack
                  label={"No.  of inputs"}
                  value={inputs ? inputs.length : cChainInputs?.length}
                  primary={true}
                />
                <div className={styles.alignMiddle}>
                  <LabelValueStack //position absolute left calc(50% + 28px);
                    label={"No. of outputs"}
                    value={outputs ? outputs.length : cChainOutputs?.length}
                    primary={true}
                  />
                </div>

                <span className={styles.alignEnd}>
                  {isExpanded ? "Hide" : "Show"} details
                </span>

              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <div className={styles.inputOutputContainer}>
                <div className={styles.grid}>
                  {printInputOutput(inputs ?? [], "input")}
                  {printInputOutput(outputs ?? [], "output")}
                  {printCChainInputOutput(cChainInputs ?? [], "input")}
                  {printCChainInputOutput(cChainOutputs ?? [], "output")}
                </div>
              </div>
            </Accordion.Panel>
          </AccordionItem>
        </Accordion>
      </>
    </>
  );
}
